// Service 14 — Medical documents, medical treatment visas and health-insurance
// certification for cross-border use. Category definitions only (client-safe).

export type MedicalFaqCategory =
  | "medical-certificate-basics"
  | "medical-records-abroad"
  | "vaccination-icvp"
  | "medical-translation-certification"
  | "mfa-embassy-legalization"
  | "medical-treatment-visa"
  | "health-insurance-visa-requirements"
  | "insurance-claims-abroad"
  | "birth-death-medical-documents"
  | "medical-poa-consent";

export interface MedicalCategoryDef {
  slug: MedicalFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const MEDICAL_FAQ_CATEGORIES: MedicalCategoryDef[] = [
  {
    slug: "medical-certificate-basics",
    titleTh: "ใบรับรองแพทย์: รูปแบบและอายุการใช้งาน",
    titleEn: "Medical Certificates: Format and Validity",
    descTh:
      "ใบรับรองแพทย์ตามแบบที่แพทยสภากำหนด ข้อมูลที่ต้องมี เลขที่ใบอนุญาตประกอบวิชาชีพเวชกรรม อายุการใช้งานที่หน่วยงานต่าง ๆ ยอมรับ และการขอฉบับภาษาอังกฤษจากโรงพยาบาล",
    descEn:
      "Thai medical certificates in the Medical Council format: mandatory fields, the physician's licence number, how long each authority accepts them, and requesting an English-language original from the hospital.",
    matchTh: /ใบรับรองแพทย์|แพทยสภา|ใบอนุญาตประกอบวิชาชีพ|อายุ.*ใบรับรอง|โรงพยาบาลออกเป็นภาษาอังกฤษ/,
    matchEn: /medical certificate|Medical Council|physician licence|validity|English original/i,
  },
  {
    slug: "medical-records-abroad",
    titleTh: "ขอประวัติการรักษาและเวชระเบียนไปใช้ต่างประเทศ",
    titleEn: "Requesting Medical Records for Use Abroad",
    descTh:
      "สิทธิของผู้ป่วยในการขอสำเนาเวชระเบียน การขอผลตรวจแล็บ ฟิล์มเอกซเรย์ และใบสรุปการรักษา (Discharge Summary) พร้อมข้อจำกัดด้าน พ.ร.บ.คุ้มครองข้อมูลส่วนบุคคล",
    descEn:
      "Patient rights to copies of medical records, lab results, imaging and discharge summaries, and how Thailand's Personal Data Protection Act affects third-party requests.",
    matchTh: /เวชระเบียน|ประวัติการรักษา|Discharge|ผลตรวจ|เอกซเรย์|PDPA|ข้อมูลส่วนบุคคล/,
    matchEn: /medical record|discharge summary|lab result|imaging|PDPA|personal data/i,
  },
  {
    slug: "vaccination-icvp",
    titleTh: "เอกสารวัคซีนและสมุดเหลือง (ICVP)",
    titleEn: "Vaccination Records and the Yellow Card (ICVP)",
    descTh:
      "หนังสือรับรองการฉีดวัคซีนระหว่างประเทศ (International Certificate of Vaccination or Prophylaxis) ตามกฎอนามัยระหว่างประเทศ วัคซีนไข้เหลืองสำหรับบางประเทศ และการรับรองเอกสารวัคซีนเป็นภาษาอังกฤษ",
    descEn:
      "The International Certificate of Vaccination or Prophylaxis under the WHO International Health Regulations, yellow fever requirements for certain destinations, and certifying vaccination records in English.",
    matchTh: /วัคซีน|สมุดเหลือง|ICVP|ไข้เหลือง|อนามัยระหว่างประเทศ/,
    matchEn: /vaccinat|yellow card|ICVP|yellow fever|health regulations/i,
  },
  {
    slug: "medical-translation-certification",
    titleTh: "แปลและรับรองเอกสารทางการแพทย์",
    titleEn: "Translating and Certifying Medical Documents",
    descTh:
      "การแปลใบรับรองแพทย์ ใบสรุปการรักษา และใบเสร็จค่ารักษาเป็นภาษาต่างประเทศ การรับรองคำแปลโดยผู้แปล การรับรองโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร และมาตรฐานศัพท์แพทย์",
    descEn:
      "Translating medical certificates, discharge summaries and hospital invoices, translator certification, Notarial Services Attorney certification, and medical terminology standards.",
    matchTh: /แปล|คำแปล|โนตารี|Notarial|ทนายความผู้ทำคำรับรอง|ศัพท์แพทย์/,
    matchEn: /translat|notar|terminology|certified translator/i,
  },
  {
    slug: "mfa-embassy-legalization",
    titleTh: "นิติกรณ์กรมการกงสุลและรับรองสถานทูตสำหรับเอกสารแพทย์",
    titleEn: "MFA Legalization and Embassy Attestation of Medical Papers",
    descTh:
      "ขั้นตอนรับรองเอกสารทางการแพทย์ที่กองสัญชาติและนิติกรณ์ กรมการกงสุล การรับรองต่อที่สถานทูต และผลของอนุสัญญา Apostille ที่จะมีผลกับไทยวันที่ 28 กุมภาพันธ์ 2570",
    descEn:
      "Legalizing medical documents at the Department of Consular Affairs, onward embassy attestation, and how the Apostille Convention entering into force for Thailand on 28 February 2027 changes the chain.",
    matchTh: /นิติกรณ์|กรมการกงสุล|สถานทูต|Apostille|2570/,
    matchEn: /legaliz|consular affairs|embassy|Apostille|2027/i,
  },
  {
    slug: "medical-treatment-visa",
    titleTh: "วีซ่าเข้ามารักษาพยาบาลในไทยและผู้ติดตาม",
    titleEn: "Medical Treatment Visas for Thailand and Accompanying Persons",
    descTh:
      "วีซ่าประเภทคนอยู่ชั่วคราวเพื่อการรักษาพยาบาล เอกสารยืนยันนัดหมายจากโรงพยาบาล การขออยู่ต่อที่สำนักงานตรวจคนเข้าเมือง และสิทธิของผู้ติดตามที่มาดูแลผู้ป่วย",
    descEn:
      "Non-Immigrant visas for medical treatment, hospital appointment confirmations, extensions of stay at the Immigration Bureau, and the position of accompanying carers.",
    matchTh: /วีซ่า|รักษาพยาบาล|ตรวจคนเข้าเมือง|อยู่ต่อ|ผู้ติดตาม/,
    matchEn: /visa|medical treatment|immigration|extension|accompanying/i,
  },
  {
    slug: "health-insurance-visa-requirements",
    titleTh: "ประกันสุขภาพที่วีซ่าไทยกำหนด",
    titleEn: "Health Insurance Required by Thai Visas",
    descTh:
      "เงื่อนไขประกันสุขภาพของวีซ่าผู้เกษียณอายุ (O-A/O-X) วีซ่าผู้พำนักระยะยาว LTR และวีซ่าอื่น ๆ วงเงินความคุ้มครอง หนังสือรับรองจากบริษัทประกัน และการตรวจสอบกรมธรรม์ต่างประเทศ",
    descEn:
      "Insurance conditions for retirement visas (O-A/O-X), the Long-Term Resident visa and other categories: coverage limits, insurer certificates and how foreign policies are verified.",
    matchTh: /ประกันสุขภาพ|กรมธรรม์|O-A|O-X|LTR|วงเงิน|คุ้มครอง/,
    matchEn: /health insurance|policy|O-A|O-X|LTR|coverage|insurer/i,
  },
  {
    slug: "insurance-claims-abroad",
    titleTh: "เคลมประกันข้ามประเทศและเอกสารค่ารักษา",
    titleEn: "Cross-Border Insurance Claims and Billing Documents",
    descTh:
      "ชุดเอกสารที่บริษัทประกันต่างประเทศเรียก ใบเสร็จรับเงินและใบสรุปค่ารักษา (Itemized Bill) ใบรับรองแพทย์ระบุการวินิจฉัย การรับรองสำเนาถูกต้อง และการรับรองลายมือชื่อผู้เรียกร้องสินไหม",
    descEn:
      "What foreign insurers ask for: receipts, itemized bills, a certificate stating the diagnosis, certified true copies and notarized claimant signatures.",
    matchTh: /เคลม|สินไหม|ใบเสร็จ|Itemized|บริษัทประกันต่างประเทศ|สำเนาถูกต้อง/,
    matchEn: /claim|itemized|receipt|insurer abroad|true copy/i,
  },
  {
    slug: "birth-death-medical-documents",
    titleTh: "เอกสารแพทย์เกี่ยวกับการเกิด การตาย และการส่งกลับ",
    titleEn: "Medical Documents for Birth, Death and Repatriation",
    descTh:
      "หนังสือรับรองการเกิดจากโรงพยาบาลกับสูติบัตร หนังสือรับรองการตายกับมรณบัตร ใบรับรองการชันสูตร และเอกสารที่ใช้ประกอบการส่งศพหรืออัฐิกลับประเทศ",
    descEn:
      "Hospital birth notifications versus birth certificates, medical death certificates versus civil death certificates, autopsy reports, and papers required to repatriate remains or ashes.",
    matchTh: /สูติบัตร|มรณบัตร|การเกิด|การตาย|ชันสูตร|ส่งศพ|อัฐิ/,
    matchEn: /birth certificate|death certificate|autopsy|repatriat|remains|ashes/i,
  },
  {
    slug: "medical-poa-consent",
    titleTh: "หนังสือมอบอำนาจและหนังสือยินยอมทางการแพทย์",
    titleEn: "Medical Powers of Attorney and Consent Letters",
    descTh:
      "หนังสือมอบอำนาจให้ดำเนินการด้านการรักษาหรือเบิกเอกสาร หนังสือยินยอมให้เด็กเดินทางหรือรับการรักษา หนังสือแสดงเจตนาไม่ประสงค์จะรับบริการที่ยืดการตาย และการรับรองลายมือชื่อ",
    descEn:
      "Powers of attorney for treatment or record collection, consent letters for a child's travel or treatment, living wills refusing life-prolonging care, and signature notarization.",
    matchTh: /มอบอำนาจ|ยินยอม|เจตนา|Living Will|ลายมือชื่อ|ผู้แทนโดยชอบธรรม/,
    matchEn: /power of attorney|consent|living will|signature|guardian/i,
  },
];

export function getMedicalCategoryDef(slug: string): MedicalCategoryDef | undefined {
  return MEDICAL_FAQ_CATEGORIES.find((c) => c.slug === slug);
}
